<script lang="ts">
  import { T, useTask, useThrelte } from '@threlte/core'
  import { Gizmo } from '@threlte/extras'
  import { PerspectiveCamera, OrthographicCamera, Vector3 } from 'three'
  import { get } from 'svelte/store'
  import {
    transformControls,
    cameraControls,
    CameraControls,
    isPerspectiveCamera,
    camera,
  } from '../../stores/camera'
  import { TransformControls } from 'three/examples/jsm/Addons.js'

  const { dom, invalidate } = useThrelte()

  // Remembered across camera type toggles so switching back to orthographic
  // doesn't lose how zoomed in the user previously was.
  let lastOrthographicZoom = 25

  $effect(() => {
    let newTransformControls: TransformControls
    let newCameraControls: CameraControls

    const previousCamera = get(camera)
    const previousControls = get(cameraControls)
    const previousPosition = previousControls?.getPosition(new Vector3())
    const previousTarget = previousControls?.getTarget(new Vector3())

    if (previousCamera instanceof OrthographicCamera) {
      lastOrthographicZoom = previousCamera.zoom
    }

    if ($isPerspectiveCamera) {
      const newCamera = new PerspectiveCamera()
      newTransformControls = new TransformControls(newCamera, dom)
      $transformControls = newTransformControls

      newCameraControls = new CameraControls(dom, newCamera)
      if (previousPosition && previousTarget) {
        newCameraControls.setLookAt(
          previousPosition.x,
          previousPosition.y,
          previousPosition.z,
          previousTarget.x,
          previousTarget.y,
          previousTarget.z,
        )
      }
      else {
        newCameraControls.setLookAt(0, -15, 30, 0, 0, 0)
      }
      newCameraControls.enableDamping = true
      newCameraControls.minDistance = 0.1
      newCameraControls.maxDistance = 80
      newCameraControls.maxPolarAngle = Math.PI / 2
      newCameraControls.boundaryFriction = 0.8
      newCameraControls.mouseButtons.left = CameraControls.ACTION.NONE
      newCameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE
      newCameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK

      $camera = newCamera
      $cameraControls = newCameraControls
    }
    else {
      const newCamera = new OrthographicCamera()
      newTransformControls = new TransformControls(newCamera, dom)
      $transformControls = newTransformControls

      newCameraControls = new CameraControls(dom, newCamera)
      // Orthographic is a strict top-down plan view: keep looking straight
      // down (only the height differs from the target) but re-center on
      // whatever the user was previously looking at instead of the origin.
      if (previousTarget) {
        newCameraControls.setLookAt(
          previousTarget.x,
          previousTarget.y,
          previousTarget.z + 10,
          previousTarget.x,
          previousTarget.y,
          previousTarget.z,
        )
      }
      else {
        newCameraControls.setLookAt(0, 0, 10, 0, 0, 0)
      }
      newCameraControls.enableDamping = true
      newCameraControls.minDistance = 5
      newCameraControls.maxDistance = 80
      newCameraControls.mouseButtons.left = CameraControls.ACTION.NONE
      newCameraControls.mouseButtons.middle = CameraControls.ACTION.TRUCK
      newCameraControls.mouseButtons.right = CameraControls.ACTION.TRUCK
      newCameraControls.zoomTo(lastOrthographicZoom)
      newCameraControls.minZoom = 0.1
      newCameraControls.maxZoom = 200

      $camera = newCamera
      $cameraControls = newCameraControls
    }

    // Dispose the previous camera controls when the camera type toggles or the
    // component unmounts, so their DOM listeners and internals don't leak.
    return () => {
      newTransformControls.dispose()
      newCameraControls.dispose()
    }
  })

  useTask(
    (delta) => {
      if ($cameraControls?.update(delta)) {
        invalidate()
      }
    },
    { autoInvalidate: false },
  )
</script>

{#if $camera}
  <T is={$camera} makeDefault />
{/if}
{#if $cameraControls && $isPerspectiveCamera}
  <Gizmo controls={$cameraControls} placement="bottom-left" />
{/if}
